/**
 * Subscription Toast Utilities
 *
 * Utilities for showing theme-aware toast notifications when users
 * hit subscription limits or feature restrictions (403 errors)
 */

import { toast } from 'react-toastify';
import React from 'react';

/**
 * Feature display names for better messaging
 */
const FEATURE_NAMES = {
  'smsNotifications': 'SMS Notifications',
  'analytics': 'Advanced Analytics',
  'customBranding': 'Custom Branding',
  'aiChatbot': 'AI Chatbot',
  'multiLocation': 'Multi-Location Management',
  'leadPipeline': 'Lead Pipeline',
  'automation': 'Automation Workflows',
  'advancedCRM': 'Advanced CRM'
};

/**
 * Plan requirements for features
 */
const FEATURE_PLANS = {
  'smsNotifications': 'Pro',
  'analytics': 'Pro',
  'customBranding': 'Pro',
  'aiChatbot': 'Enterprise',
  'multiLocation': 'Enterprise',
  'leadPipeline': 'Pro',
  'automation': 'Pro',
  'advancedCRM': 'Pro'
};

/**
 * Custom toast component with upgrade button
 */
const UpgradeToastContent = ({ message, feature, onUpgrade }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <div style={{ fontWeight: 600 }}>{message}</div>
    <button
      onClick={onUpgrade}
      style={{
        padding: '8px 16px',
        backgroundColor: '#1976d2',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '14px',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
      onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
    >
      Upgrade Now
    </button>
  </div>
);

/**
 * Show a subscription upgrade toast
 *
 * @param {string} feature - The feature that requires upgrade
 * @param {string} currentPlan - User's current plan
 * @param {Function} navigate - React Router navigate function (optional)
 */
export const showSubscriptionToast = (feature, currentPlan = 'Free', navigate = null) => {
  const featureName = FEATURE_NAMES[feature] || feature;
  const requiredPlan = FEATURE_PLANS[feature] || 'Pro';

  const message = `${featureName} requires ${requiredPlan} plan`;

  const handleUpgrade = () => {
    if (navigate) {
      navigate('/settings?tab=billing');
    } else {
      window.location.href = '/settings?tab=billing';
    }
    toast.dismiss(); // Dismiss all toasts when navigating
  };

  toast.warning(
    <UpgradeToastContent
      message={message}
      feature={feature}
      onUpgrade={handleUpgrade}
    />,
    {
      autoClose: 7000,
      closeButton: true,
      position: 'top-center',
      style: {
        minWidth: '320px'
      }
    }
  );
};

/**
 * Show a limit reached toast
 *
 * @param {string} limitType - The type of limit (e.g., 'maxServices', 'maxAppointments')
 * @param {number} limit - The current limit
 * @param {number} current - Current count (optional)
 * @param {Function} navigate - React Router navigate function (optional)
 */
export const showLimitToast = (limitType, limit, current = null, navigate = null) => {
  const limitName = FEATURE_NAMES[limitType] || limitType;

  // Special handling for monthly appointment limits
  let message;
  if (limitType === 'maxAppointments' || limitType === 'appointments') {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const resetDate = nextMonth.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });

    message = current !== null
      ? `Monthly Limit Reached: ${current}/${limit} appointments used this month. Resets ${resetDate}. Upgrade for unlimited appointments!`
      : `Monthly Limit: Your plan allows ${limit} appointments per month. Resets ${resetDate}. Upgrade for unlimited!`;
  } else {
    message = current !== null
      ? `Limit Reached: ${current}/${limit} ${limitName.toLowerCase()}. Upgrade for more!`
      : `You've reached your limit of ${limit} ${limitName.toLowerCase()}`;
  }

  const handleUpgrade = () => {
    if (navigate) {
      navigate('/pricing');
    } else {
      window.location.href = '/pricing';
    }
    toast.dismiss();
  };

  toast.warning(
    <UpgradeToastContent
      message={message}
      feature={limitType}
      onUpgrade={handleUpgrade}
    />,
    {
      autoClose: 10000, // Longer duration for important limit message
      closeButton: true,
      position: 'top-center',
      style: {
        minWidth: '380px'
      }
    }
  );
};

/**
 * Show a generic 403 error toast with upgrade option
 *
 * @param {string} message - Custom error message
 * @param {Function} navigate - React Router navigate function (optional)
 */
export const show403Toast = (message = 'This feature is not available on your current plan', navigate = null) => {
  const handleUpgrade = () => {
    if (navigate) {
      navigate('/settings?tab=billing');
    } else {
      window.location.href = '/settings?tab=billing';
    }
    toast.dismiss();
  };

  toast.error(
    <UpgradeToastContent
      message={message}
      feature="premium"
      onUpgrade={handleUpgrade}
    />,
    {
      autoClose: 7000,
      closeButton: true,
      position: 'top-center',
      style: {
        minWidth: '320px'
      }
    }
  );
};

/**
 * Parse error response and show appropriate toast
 *
 * @param {Response} response - Fetch API response object
 * @param {Function} navigate - React Router navigate function (optional)
 */
export const handleSubscriptionError = async (response, navigate = null) => {
  if (response.status === 402) {
    try {
      const errorData = await response.json();
      if (errorData.code === 'INSUFFICIENT_CREDITS') {
        toast.warning(
          <UpgradeToastContent
            message={errorData.message || 'Insufficient credits for this operation'}
            feature="credits"
            onUpgrade={() => {
              if (navigate) navigate('/settings?tab=billing');
              else window.location.href = '/settings?tab=billing';
              toast.dismiss();
            }}
          />,
          { autoClose: 6000, closeButton: true, position: 'top-center', style: { minWidth: '320px' } }
        );
      } else {
        // FEATURE_LOCKED via 402
        showSubscriptionToast(errorData.feature || 'premium', errorData.currentPlan, navigate);
      }
    } catch (e) {
      show403Toast('This feature requires credits or a subscription upgrade', navigate);
    }
    return true;
  }

  if (response.status === 403) {
    try {
      const errorData = await response.json();

      // Check if error message contains feature information
      if (errorData.feature) {
        showSubscriptionToast(errorData.feature, errorData.currentPlan, navigate);
      } else if (errorData.limitType || errorData.limit) {
        // NEW: Pass current count for better messaging
        showLimitToast(
          errorData.limitType || 'appointments',
          errorData.limit,
          errorData.current || null,
          navigate
        );
      } else {
        show403Toast(errorData.error || errorData.message, navigate);
      }
    } catch (e) {
      // If JSON parsing fails, show generic message
      show403Toast('This feature requires a subscription upgrade', navigate);
    }

    return true; // Handled
  }

  return false; // Not a 403 error
};

/**
 * Enhanced API fetch wrapper that automatically handles subscription errors
 *
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options
 * @param {Function} navigate - React Router navigate function (optional)
 */
export const fetchWithSubscriptionHandling = async (url, options = {}, navigate = null) => {
  const response = await fetch(url, options);

  // Check for subscription errors
  const wasHandled = await handleSubscriptionError(response, navigate);

  if (wasHandled) {
    // Throw a specific error that calling code can check for
    const error = new Error('Subscription required');
    error.isSubscriptionError = true;
    error.status = 403;
    throw error;
  }

  // For other errors, throw as normal
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || error.message || `API error: ${response.statusText}`);
  }

  return response;
};

export default {
  showSubscriptionToast,
  showLimitToast,
  show403Toast,
  handleSubscriptionError,
  fetchWithSubscriptionHandling
};
